import { Badge, BlockStack, Card, InlineStack, Select, Text } from '@shopify/polaris';
import { StyledTemplateHeading } from './styled';
import Switch from '@/components/Switch';
import { useDispatch, useSelector } from 'react-redux';
import emailSlice, {
  emailTemplatesSelector,
  emailTemplatesTabSelector,
  orderStatusSelector,
} from '@/redux/features/email.slice';
import { PATH, TrackingStatus } from '@/constants';
import { transformOptions } from '@/helpers';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { sendToOptions } from './config';

const TemplateHeading = () => {
  const dispatch = useDispatch();
  const navigate = useConfirmationNavigate();
  const orderStatusState = useSelector(orderStatusSelector);
  const templatesTab = useSelector(emailTemplatesTabSelector);
  const templateState = useSelector(emailTemplatesSelector);

  const orderStatusOptions = transformOptions(TrackingStatus).filter(({ value }) => value !== TrackingStatus.All);

  const isEnabled = templateState[templatesTab].status.some((status) => status === orderStatusState);

  const handleStatusChange = (value: string) => {
    // Order status is derived from the route param (see EmailTemplatesDetail), so a
    // confirmed navigation both switches templates and triggers the unsaved-changes
    // dialog when dirty. Keep the active receiver in the URL so the route stays the
    // single source of truth (changing status must not drop back to a stale receiver).
    navigate(`${PATH.NOTIFICATIONS_EMAIL}/${value}?receiver=${templatesTab}`);
  };

  const handleSendToChange = (value: string) => {
    // "Send to" switches the customer/merchant template. Drive it through the route
    // (carried as ?receiver=, synced back in EmailTemplatesDetail) — exactly like
    // Status — so a dirty form goes through the custom "unsaved changes" confirm
    // modal. The previous native leaveConfirmation() is a no-op in this App Bridge
    // <SaveBar> setup, which left the dropdown dead (no confirm, no switch).
    navigate(`${PATH.NOTIFICATIONS_EMAIL}/${orderStatusState}?receiver=${value}`);
  };

  // Toggling "Enabled" is a form field, not an instant action: it optimistically
  // updates the status array so the contextual save bar appears (dirty). The actual
  // persist happens on Save (TemplateEditor.handleSave already derives isActive from
  // this array); Discard reverts it. No eager updateTemplate here — that used to
  // refetch and wipe in-progress Subject/Content edits.
  const handleChangeSwitch = (newValue: boolean, key: TrackingStatus) => {
    const currentStatus = templateState[templatesTab].status;
    const updatedStatus = newValue
      ? currentStatus.includes(key)
        ? currentStatus
        : [...currentStatus, key]
      : currentStatus.filter((status) => status !== key);
    dispatch(
      emailSlice.actions.handleChangeEmailTemplates({
        ...templateState,
        [templatesTab]: { ...templateState[templatesTab], status: updatedStatus },
      }),
    );
  };

  return (
    <StyledTemplateHeading>
      <BlockStack gap="400">
        <Card>
          <InlineStack align="space-between" blockAlign="center" gap="400">
            <InlineStack gap="200">
              <Text variant="bodyMd" as="p">
                Enabled
              </Text>
              <Badge tone={isEnabled ? 'success' : undefined}>{isEnabled ? 'On' : 'Off'}</Badge>
              <Switch isActive={isEnabled} onClick={() => handleChangeSwitch(!isEnabled, orderStatusState)} />
            </InlineStack>
            <InlineStack gap="800">
              <InlineStack gap="200">
                <Text variant="bodyMd" as="p">
                  Status
                </Text>
                <Select
                  label=""
                  labelHidden
                  options={orderStatusOptions}
                  value={orderStatusState}
                  onChange={handleStatusChange}
                />
              </InlineStack>
              <InlineStack gap="200">
                <Text variant="bodyMd" as="p">
                  Send to
                </Text>
                <Select label="" labelHidden options={sendToOptions} value={templatesTab} onChange={handleSendToChange} />
              </InlineStack>
            </InlineStack>
          </InlineStack>
        </Card>
      </BlockStack>
    </StyledTemplateHeading>
  );
};

export default TemplateHeading;
